import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  Target,
  Headphones,
  Megaphone,
  Building2,
  Landmark,
  ShoppingBag,
  Cpu,
  Handshake,
} from "lucide-react";
import { buildSeo } from "@/lib/seo";
import {
  Eyebrow,
  MarketingLayout,
  SectionHeader,
  useHydratedTranslation,
} from "@/components/marketing/Layout";

// Topical hub: one strong page that covers every angle (roles + industries)
// and internally links to the blog + product. Better SEO than many thin pages.
// Copy is localized via routes.useCases; only icon + internal link live here.

const ROLES = [
  { icon: Target, link: "/blog/convert-website-visitors-with-ai" },
  { icon: Headphones, link: "/blog/ai-customer-support-automation" },
  { icon: Megaphone, link: "/platform" },
  { icon: Building2, link: "/blog/ai-twins-for-agencies" },
] as const;

const INDUSTRIES = [{ icon: Cpu }, { icon: Landmark }, { icon: ShoppingBag }, { icon: Handshake }];

export const Route = createFileRoute("/use-cases")({
  head: () => {
    const seo = buildSeo({
      path: "/use-cases",
      title: "AI Twin use cases: sales, support & marketing | Reality Twin",
      description:
        "How companies use AI Twins across sales, customer support, marketing and agencies — and by industry: SaaS, professional services, e-commerce and B2B.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Reality Twin use cases",
            url: "https://realitytwin.io/use-cases",
            description:
              "AI Twin use cases across sales, support, marketing, agencies and industries.",
          }),
        },
      ],
    };
  },
  component: UseCasesPage,
});

function UseCasesPage() {
  const t = useHydratedTranslation();
  const roleT = t("routes.useCases.roles", { returnObjects: true }) as unknown as {
    title: string;
    desc: string;
    linkLabel: string;
  }[];
  const industryT = t("routes.useCases.industries", { returnObjects: true }) as unknown as {
    title: string;
    desc: string;
  }[];
  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <Eyebrow>{t("routes.useCases.eyebrow")}</Eyebrow>
          <h1 className="mt-4 font-serif text-[44px] sm:text-[68px] leading-[0.98] tracking-[-0.035em] max-w-3xl">
            {t("routes.useCases.titleA")}
            <em className="italic text-muted-foreground">{t("routes.useCases.titleEm")}</em>
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-[1.55] text-muted-foreground">
            {t("routes.useCases.sub")}
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
            >
              {t("routes.useCases.getStarted")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center rounded-full hairline bg-card px-5 py-3 text-[14px] hover:bg-cream"
            >
              {t("routes.useCases.seePricing")}
            </Link>
          </div>
        </div>
      </section>

      {/* By role */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("routes.useCases.byTeamEyebrow")}
            title={
              <>
                {t("routes.useCases.byTeamTitleA")}
                <em className="italic text-muted-foreground">
                  {t("routes.useCases.byTeamTitleEm")}
                </em>
              </>
            }
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
            {ROLES.map((r, i) => (
              <div key={r.link} className="bg-card p-7 sm:p-8 flex flex-col">
                <r.icon className="h-5 w-5" strokeWidth={1.5} />
                <h2 className="mt-5 font-serif text-[24px] leading-tight tracking-[-0.02em]">
                  {roleT[i]?.title}
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-muted-foreground flex-1">
                  {roleT[i]?.desc}
                </p>
                <Link
                  to={r.link}
                  className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-foreground hover:gap-2 transition-all"
                >
                  {roleT[i]?.linkLabel} <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By industry */}
      <section className="bg-cream border-y border-border py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("routes.useCases.byIndustryEyebrow")}
            title={
              <>
                {t("routes.useCases.byIndustryTitleA")}
                <em className="italic text-muted-foreground">
                  {t("routes.useCases.byIndustryTitleEm")}
                </em>
              </>
            }
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <div key={i} className="rounded-2xl border border-border bg-background p-6">
                <ind.icon className="h-5 w-5" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-[19px] leading-tight">{industryT[i]?.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
                  {industryT[i]?.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-[36px] sm:text-[48px] leading-[1.02] tracking-[-0.025em]">
            {t("routes.useCases.ctaTitle")}
          </h2>
          <p className="mt-5 text-[16px] text-muted-foreground max-w-md mx-auto">
            {t("routes.useCases.ctaSub")}
          </p>
          <div className="mt-8 flex justify-center gap-2.5">
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
            >
              {t("routes.useCases.getStarted")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center rounded-full hairline bg-card px-5 py-3 text-[14px] hover:bg-cream"
            >
              {t("routes.useCases.readBlog")}
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
